import {
  UseFormRegister,
  RegisterOptions,
  UseFormClearErrors,
  UseFormSetValue,
} from "react-hook-form";
import { useState, useEffect, useRef, useCallback } from "react";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { FaSpinner } from "react-icons/fa";
import FormErrorMessage from "./common/FormErrorMessage";

type SelectOption = {
  id?: string | number;
  value: string | number;
  text: string;
  img?: string;
};

type Props = {
  name: string;
  label?: string;
  placeholder?: string;
  selectOptions: SelectOption[];
  register?: UseFormRegister<any>;
  validation?: RegisterOptions;
  error?: any;
  className?: string;
  labelClass?: string;
  disabled?: boolean;
  removeDiv?: boolean;
  value?: string | any;
  setValue?: (val: string | any) => void;
  onChange?: (val: string | any) => void;
  clearErrors?: UseFormClearErrors<any>;
  formSetValue?: UseFormSetValue<any>;
  onSearch?: (query: string) => void; // 🔹 API search trigger
  isPending?: boolean; // 🔹 show spinner while loading
  allowCustom?: boolean; // 🔹 allow custom text input
};

export default function SelectInput({
  name,
  label,
  placeholder = "Select",
  selectOptions,
  register,
  validation,
  error,
  className = "",
  labelClass = "",
  disabled = false,
  removeDiv = false,
  value,
  setValue,
  onChange,
  clearErrors,
  formSetValue,
  onSearch,
  isPending,
  allowCustom,
}: Props) {
  const [selectOpen, setSelectOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // selected option text
  const selectedText =
    selectOptions?.find((opt) => opt?.value === value)?.text || (allowCustom && typeof value === 'string' ? value : "");

  // Whether a real value is selected (for showing clear button)
  const hasValue = !!(value && value !== "");

  // filter locally — when focused with no typed text, show all options
  const filteredOptions = searchText
    ? selectOptions?.filter((opt) =>
      opt?.text?.toLowerCase().includes(searchText?.toLowerCase())
    )
    : selectOptions;

  const handleSelect = (val: string | number) => {
    if (setValue) setValue(val);
    if (onChange) onChange(val);
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, val);
    setSelectOpen(false);
    setSearchText(""); // reset search after select
    setIsFocused(false);
  };

  const handleClear = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (setValue) setValue("");
    if (onChange) onChange("");
    if (clearErrors) clearErrors(name);
    if (formSetValue) formSetValue(name, "");
    setSearchText("");
    setSelectOpen(false);
    setIsFocused(false);
  }, [setValue, onChange, clearErrors, formSetValue, name]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSelectOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropdownToggle = () => {
    if (disabled) return;

    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setOpenUpward(spaceBelow < 250 && spaceAbove > 250);
    }

    setSelectOpen((prev) => !prev);
  };

  // 🔹 handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchText(query);
    if (!selectOpen) setSelectOpen(true);

    if (onSearch && query.trim().length >= 3) {
      onSearch(query.trim());
    }
  };

  // When the input gains focus: clear searchText so user types into fresh search
  // (fixes "first letter acts as backspace" — previously selectedText was shown
  // inside the input value, so the first keystroke would replace all visible text)
  const handleInputFocus = () => {
    setIsFocused(true);
    setSearchText(""); // always start fresh when focusing
    setSelectOpen(true);
  };

  const handleInputBlur = () => {
    // Delay so click on dropdown item can fire first
    setTimeout(() => setIsFocused(false), 150);
  };

  const labelNode = label && (
    <label
      htmlFor={name}
      className={`text-[#1E293B] text-sm ${labelClass} whitespace-nowrap`}
    >
      {label}
      {validation?.required && <span className="text-red-600 ml-1">*</span>}
    </label>
  );

  const dropdown = (
    <div
      className={`absolute z-30 left-0 w-full rounded-xl bg-white ${searchText?.length >= 3 && "border border-gray-200"
        } shadow-lg max-h-60 overflow-y-auto ${openUpward ? "bottom-full mb-1" : "top-full mt-1"
        }`}
    >
      <ul>
        {filteredOptions?.map((opt, i) => (
          <li
            key={opt.id ?? i}
            className={`px-4 py-2 text-sm hover:bg-blue-100 cursor-pointer flex items-center gap-2 ${opt.value === value ? "bg-blue-50 font-medium" : ""
              }`}
            onClick={() => handleSelect(opt.value)}
          >
            {opt?.img && <img src={opt.img} className="size-8 rounded-md object-cover" />}

            {opt.text}
          </li>
        ))}

        {/* Show "No results" only when user typed */}
        {searchText && filteredOptions?.length === 0 && !allowCustom && (
          <li className="px-4 py-2 text-sm text-gray-400">No results found</li>
        )}

        {/* Custom text entry */}
        {searchText && allowCustom && !filteredOptions?.some(o => o.text.toLowerCase() === searchText.toLowerCase()) && (
          <li 
            className="px-4 py-2 text-sm hover:bg-blue-100 cursor-pointer flex items-center gap-2 text-blue-600"
            onClick={() => handleSelect(searchText)}
          >
            Use "{searchText}"
          </li>
        )}
      </ul>
    </div>
  );

  const selectField = (
    <div ref={dropdownRef} className="relative w-full">
      <div
        className={`flex items-center justify-between px-4 py-2 text-sm rounded-full border ${error ? "border-red-500" : "border-gray-200"
          }  ${disabled ? "opacity-70 bg-gray-100" : "cursor-pointer bg-white"}`}
        onClick={handleDropdownToggle}
      >
        {/* 🔹 Inline search input — value shows searchText when typing, or selectedText when idle */}
        <input
          ref={inputRef}
          type="text"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400 min-w-0"
          placeholder={placeholder}
          value={isFocused ? searchText : selectedText}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          autoComplete="new-password"
          onClick={(e) => e.stopPropagation()}
        />

        {/* 🔹 Spinner when searching */}
        {searchText?.length >= 3 && isPending && (
          <FaSpinner className="animate-spin text-gray-500 ml-2 flex-shrink-0" size={16} />
        )}

        {/* ✕ Clear button — only shown when a value is selected and not loading */}
        {hasValue && !isPending && (
          <button
            type="button"
            onMouseDown={handleClear}
            className="flex-shrink-0 ml-1 text-gray-400 hover:text-red-500 transition-colors"
            tabIndex={-1}
            aria-label="Clear selection"
          >
            <IconX size={14} stroke={2.5} />
          </button>
        )}

        <IconChevronDown
          size={18}
          stroke={2.2}
          className={`text-gray-500 transition-transform flex-shrink-0 ml-1 ${selectOpen ? "rotate-180" : ""
            }`}
        />
      </div>

      {selectOpen && dropdown}

      {register && !formSetValue && (
        <input
          autoComplete="off"
          type="hidden"
          {...register(name, validation)}
          value={value ?? ""}
        />
      )}
    </div>
  );

  return (
    <div className={`space-y-1 relative ${className}`}>
      {labelNode}
      <div className="w-full">
        {removeDiv ? (
          selectField
        ) : (
          <div className="relative">{selectField}</div>
        )}
        <FormErrorMessage error={error} />
      </div>
    </div>
  );
}
